import {
  Intervention,
  InterventionTag,
  PathwayIntervention,
  PathwayScenarioWithInventory,
} from '@/types';
import { useCallback, useState } from 'react';
import { useInterventionKey } from '@/hooks/use-intervention-key';
import { Link } from 'react-router-dom';
import { noop, isEmpty } from 'lodash/fp';
import {
  Anchor,
  Text,
  Stack,
  LabelText,
  Secondary,
  Inline,
  InlineItem,
  UtilityButton,
  Icon,
} from '@kinesis/bungle';
import { Detail } from './detail';
import { CopyIntervention } from '@/components/copy-intervention';
import { contextColors } from '@/data/colors';
import { interventionTags } from '@/data/interventions';
import { helpFor } from '@/data/help';

// The ambition dot(s): legacy interventions carry one tag; new-model
// pathway interventions carry the explicit set — one dot per included
// evaluation, in ambition order.
const tagsOf = (
  intervention: Intervention | PathwayIntervention,
): InterventionTag[] =>
  'tags' in intervention
    ? interventionTags.filter((tag) => intervention.tags.includes(tag))
    : [intervention.tag];

const SidebarIntervention = (props: SidebarInterventionProps) => {
  const {
    detailPath,
    intervention,
    onUpdateIntervention = noop,
    enableCopy = false,
    scenarios = [],
    onCopyIntervention = async () => {},
  } = props;
  const interventionKey = useInterventionKey();
  const [showCopy, setShowCopy] = useState(false);
  const openCopy = useCallback(() => setShowCopy(true), []);
  const closeCopy = useCallback(() => setShowCopy(false), []);
  const onUpdate = useCallback(
    (f: (x: any) => any) => {
      return onUpdateIntervention(intervention.id, f);
    },
    [intervention.id, onUpdateIntervention],
  );
  return (
    <Stack space='medium'>
      <Stack space='xsmall'>
        <Stack space='none'>
          <Inline space='small'>
            {tagsOf(intervention).map((tag) => (
              <InlineItem key={tag} sizing='fit-content'>
                <Icon
                  color={contextColors[tag]}
                  magnitude='xsmall'
                  type='circle'
                />
              </InlineItem>
            ))}
            <InlineItem sizing='fill-container'>
              <LabelText as='label'>{intervention.name}</LabelText>
            </InlineItem>
            <InlineItem sizing='fit-content'>
              <Inline space='xsmall'>
                <InlineItem sizing='fit-content'>
                  <UtilityButton
                    icon='bulb'
                    magnitude='small'
                    as={Anchor}
                    variant='default'
                    href={helpFor(intervention)}
                    target='_blank'
                  />
                </InlineItem>

                {enableCopy && (
                  <InlineItem sizing='fit-content'>
                    <UtilityButton
                      icon='copy'
                      magnitude='small'
                      variant='default'
                      onClick={openCopy}
                    />
                  </InlineItem>
                )}

                <InlineItem sizing='fit-content'>
                  <UtilityButton
                    icon='control'
                    magnitude='small'
                    as={Link}
                    variant={
                      interventionKey == intervention.id ? 'accent' : 'default'
                    }
                    to={
                      interventionKey == intervention.id
                        ? detailPath
                        : `${detailPath}/interventions/${intervention.id}`
                    }
                  />
                </InlineItem>
              </Inline>
            </InlineItem>
          </Inline>
          <LabelText as='label'>
            <Secondary>{intervention.description}</Secondary>
          </LabelText>
        </Stack>
        {!isEmpty(intervention.link) && (
          <Inline space='xsmall'>
            <InlineItem sizing='fit-content'>
              <Text tone='secondary'>Reference: </Text>
            </InlineItem>
            <InlineItem sizing='fill-container'>
              <Anchor href={intervention.link} target='_blank'>
                <Inline space='xsmall'>
                  <InlineItem ellipsify>{intervention.link}</InlineItem>
                  <InlineItem>
                    <Icon type='external-link' />
                  </InlineItem>
                </Inline>
              </Anchor>
            </InlineItem>
          </Inline>
        )}
      </Stack>
      <Detail intervention={intervention} onUpdate={onUpdate} />
      {showCopy && (
        <CopyIntervention
          intervention={intervention as Intervention}
          onClose={closeCopy}
          scenarios={scenarios}
          onCopy={onCopyIntervention}
        />
      )}
    </Stack>
  );
};

export type SidebarInterventionProps = {
  // The absolute path of the owning detail page — the legacy pages pass
  // `pathwayScenarioPath(...)`, the new pathway page `pathwayPath(...)`;
  // the intervention links hang off it.
  detailPath: string;
  intervention: Intervention | PathwayIntervention;
  onUpdateIntervention?: (id: string | number, v: (x: any) => any) => void;
  enableCopy?: boolean;
  scenarios?: PathwayScenarioWithInventory[];
  onCopyIntervention?: (
    scenarioId: number,
    intervention: Intervention,
  ) => Promise<void>;
};

export { SidebarIntervention };
